import React from 'react';

// Styles & Assets
import styles from './DraggableList.module.css';

// Other
import { DraggingStyle, NotDraggingStyle, DropResult, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

interface Props<T> {
    onDragEnd: (result: DropResult) => void
    droppableId: string
    orderedList: T[]
    children: (item: T, index: number, dragHandleProps: DraggableProvidedDragHandleProps | null | undefined) => React.ReactNode
}

interface UUID {
    uuid?: string
}
interface ID {
    id?: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasUUIDProperty(obj: any): obj is UUID {
    return obj && typeof obj === 'object' && 'uuid' in obj;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasIDProperty(obj: any): obj is ID {
    return obj && typeof obj === 'object' && 'id' in obj;
}

const DragDropContext = React.lazy(() =>
    import('react-beautiful-dnd').then(module => {
        return { default: module.DragDropContext };
    })
);
const Droppable = React.lazy(() =>
    import('react-beautiful-dnd').then(module => {
        return { default: module.Droppable };
    })
);
const Draggable = React.lazy(() =>
    import('react-beautiful-dnd').then(module => {
        return { default: module.Draggable };
    })
);

const DraggableList = <T,>({ droppableId, orderedList, onDragEnd, children }: Props<T>) => {


    const getKeyItem = (item: T) => {
        if (hasUUIDProperty(item) && item.uuid !== undefined) return item.uuid;
        if (hasIDProperty(item) && item.id !== undefined) return item.id;
        if (typeof item === 'number' || typeof item === 'string') return item;
        return crypto.randomUUID();
    };

    const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle | undefined) => ({
        boxShadow: (isDragging) ?
            '0 5px 5px -3px rgb(0, 0, 0, 0.2), 0 8px 10px 1px rgb(0, 0, 0, 0.14), 0 3px 5px 2px rgb(0, 0, 0, 0.12)' :
            undefined
        , borderRadius: '4px'
        , backgroundColor: '#fff'
        , ...draggableStyle
    });

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={droppableId}>
                {(provided) => (
                    <ul {...provided.droppableProps} ref={provided.innerRef} className={styles.listContainer}>
                        {orderedList.map((item, index) =>
                            <Draggable
                                key={getKeyItem(item)}
                                draggableId={getKeyItem(item).toString()}
                                index={index}
                            >
                                {(provided, snapshot) => (
                                    <li className={styles.listItem}
                                        {...provided.draggableProps}
                                        ref={provided.innerRef}
                                        style={getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style
                                        )}
                                    >
                                        {children(item, index, provided.dragHandleProps)}
                                    </li>
                                )}
                            </Draggable>
                        )}
                        {provided.placeholder}
                    </ul>
                )}
            </Droppable>

        </DragDropContext>
    );
};

export default DraggableList;